import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  ExternalLink,
  Eye,
  FileWarning,
  HardDrive,
  Loader2,
  Trash2,
} from 'lucide-react';
import {
  fetchFichiers,
  lienApercuFichier,
  supprimerFichier,
  type FichierDepose,
  type RapportDestruction,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';

const poids = (octets: number): string => {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} Ko`;
  return `${(octets / (1024 * 1024)).toFixed(1)} Mo`;
};

const jourCourt = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/** Ce qui se regarde dans le navigateur, et ce qui se télécharge. */
const REGARDABLE = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'pdf', 'txt'];

/**
 * Ce qui occupe le quota, et comment en reprendre.
 *
 * Le compteur de documents monte sans qu'on sache de quoi. Un président reçoit
 * « votre espace est plein à 92 % » et n'a aucun moyen de savoir si ce sont
 * les affiches de 2019, les pièces d'adhérents partis, ou quinze essais de
 * logo téléversés le même soir. Il paie une extension par défaut
 * d'information, ce qui est la pire des raisons de payer.
 *
 * L'écran montre le détail et sépare les orphelins : un fichier qu'aucune
 * fiche ne référence ne s'affichera plus jamais nulle part, il ne reste que
 * dans le compteur. Ce sont ceux-là qu'on supprime sans rien perdre — et
 * c'est là qu'est la place à récupérer, pas dans un tri à l'aveugle.
 */
export default function FichiersPage() {
  const { isLoading: authLoading, aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const peut = aLeDroit('gerer_fichiers');

  const [ouvert, setOuvert] = useState<string | null>(null);
  const [regarde, setRegarde] = useState<FichierDepose | null>(null);
  const [aDetruire, setADetruire] = useState<FichierDepose | null>(null);
  const [rapports, setRapports] = useState<RapportDestruction[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [orphelinsSeuls, setOrphelinsSeuls] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['fichiers-deposes'],
    queryFn: fetchFichiers,
    enabled: peut,
  });

  const detruire = useMutation({
    mutationFn: (chemin: string) => supprimerFichier(chemin),
    onSuccess: (r: any) => {
      setErreur(null);
      setADetruire(null);
      setRegarde(null);
      // Le procès-verbal s'empile : on supprime rarement une seule pièce, et
      // la liste de ce qui vient de partir est ce qu'on relit après coup.
      setRapports((prec) => [r.rapport, ...prec]);
      queryClient.invalidateQueries({ queryKey: ['fichiers-deposes'] });
      queryClient.invalidateQueries({ queryKey: ['sauvegardes'] });
    },
    onError: (e: Error) => setErreur(e.message),
  });

  if (authLoading || (peut && isPending)) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!peut) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              La gestion des fichiers déposés est réservée aux administrateurs. Elle ouvre la
              suppression définitive de pièces que d'autres ont déposées : elle ne se donne pas
              par défaut à une fonction au motif qu'elle gère autre chose.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const total = data?.total;
  const quota = data?.quota;

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <HardDrive className="w-7 h-7 text-primary-700 shrink-0" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Fichiers déposés
          </h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Ce qui occupe votre quota, pièce par pièce — et ce dont plus personne ne se sert.
          </p>
        </div>
      </div>

      {/* Le quota d'abord : sans lui, on ne sait pas si les trois mégaoctets
          qu'on regarde sont un problème ou rien du tout. */}
      {quota && (
        <div className="card space-y-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {poids(quota.utilise)} occupés sur {poids(quota.total)}
            </span>
            <span
              className={`text-sm font-bold tabular-nums ${
                quota.part >= 90 ? 'text-red-600' : quota.part >= 75 ? 'text-amber-600' : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              {quota.part} %
            </span>
          </div>
          <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <div
              className={`h-full transition-all ${
                quota.part >= 90 ? 'bg-red-500' : quota.part >= 75 ? 'bg-amber-500' : 'bg-primary-600'
              }`}
              style={{ width: `${Math.min(100, quota.part)}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Les sauvegardes nocturnes ne sont pas comptées ici : elles vivent chez l'éditeur, hors
            de votre quota.
          </p>
        </div>
      )}

      {/* Les orphelins : c'est là qu'est la place, et nulle part ailleurs. */}
      {(total?.orphelins ?? 0) > 0 && (
        <div className="card bg-primary-50 border-primary-200 dark:bg-primary-900/20 dark:border-primary-800 flex flex-wrap items-center gap-3">
          <FileWarning className="w-5 h-5 text-primary-700 shrink-0" />
          <p className="text-sm text-primary-900 dark:text-primary-200 flex-1 min-w-[16rem]">
            <strong>
              {total!.orphelins} fichier{total!.orphelins > 1 ? 's' : ''} — {poids(total!.octetsOrphelins)}
            </strong>{' '}
            ne {total!.orphelins > 1 ? 'sont' : 'est'} référencé{total!.orphelins > 1 ? 's' : ''} par
            aucune fiche. {total!.orphelins > 1 ? 'Ils ne s’afficheront' : 'Il ne s’affichera'} plus
            nulle part : {total!.orphelins > 1 ? 'ils occupent' : 'il occupe'} de la place, et rien
            d'autre.
          </p>
          <button
            type="button"
            onClick={() => setOrphelinsSeuls(!orphelinsSeuls)}
            className={`text-sm px-3 py-1.5 rounded-lg font-medium ${
              orphelinsSeuls
                ? 'bg-primary-600 text-white'
                : 'btn-outline'
            }`}
          >
            {orphelinsSeuls ? 'Voir tout' : 'Ne montrer que ceux-là'}
          </button>
        </div>
      )}

      {erreur && (
        <div className="card bg-red-50 border-red-200 text-red-800 text-sm flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <p>{erreur}</p>
        </div>
      )}

      {/* Le procès-verbal de ce qui vient d'être détruit. Il reste à l'écran :
          la suppression est irréversible, et ce qui a disparu doit pouvoir se
          nommer après coup — ne serait-ce que pour savoir quoi redemander. */}
      {rapports.length > 0 && (
        <section className="card space-y-2 border-red-200 dark:border-red-800">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-red-600" />
            Rapport de destruction
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {rapports.length} pièce{rapports.length > 1 ? 's' : ''} détruite
            {rapports.length > 1 ? 's' : ''} pendant cette session, pour un total de{' '}
            {poids(rapports.reduce((s, r) => s + r.octets, 0))} récupérés. Cette liste disparaît en
            quittant l'écran.
          </p>
          <ul className="text-sm divide-y divide-gray-100 dark:divide-gray-800">
            {rapports.map((r) => (
              <li key={r.empreinte + r.detruitLe} className="py-2 space-y-0.5">
                <p className="font-medium text-gray-900 dark:text-gray-100">{r.nom}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {poids(r.octets)} · {r.type || 'sans extension'} · déposé le{' '}
                  {jourCourt.format(new Date(r.modifie))} ·{' '}
                  {r.utilisePar ? `était une ${r.utilisePar}` : 'orphelin'} · empreinte{' '}
                  <code className="font-mono">{r.empreinte}</code>
                </p>
                <p className="text-xs text-gray-400 font-mono">{r.chemin}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(data?.groupes ?? []).map((groupe) => {
        const fichiers = orphelinsSeuls ? groupe.fichiers.filter((f) => f.orphelin) : groupe.fichiers;
        if (fichiers.length === 0) return null;
        const deplie = ouvert === groupe.code;

        return (
          <section key={groupe.code} className="card">
            <button
              type="button"
              onClick={() => setOuvert(deplie ? null : groupe.code)}
              className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 text-left"
            >
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {groupe.libelle}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {groupe.nombre} fichier{groupe.nombre > 1 ? 's' : ''}
              </span>
              {groupe.orphelins > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  {groupe.orphelins} orphelin{groupe.orphelins > 1 ? 's' : ''}
                </span>
              )}
              <span className="ml-auto font-semibold tabular-nums text-gray-700 dark:text-gray-300">
                {poids(groupe.octets)}
              </span>
              <span className="text-gray-400 text-sm">{deplie ? '▲' : '▼'}</span>
            </button>

            {deplie && (
              <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-800 text-sm">
                {fichiers.map((f) => (
                  <li key={f.chemin} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="min-w-0 flex-1">
                      <span className="block text-gray-900 dark:text-gray-100 truncate">{f.nom}</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        {jourCourt.format(new Date(f.modifie))} ·{' '}
                        {f.orphelin ? (
                          <span className="text-amber-700 dark:text-amber-400">
                            plus référencé nulle part
                          </span>
                        ) : (
                          <>utilisé comme {f.utilisePar}</>
                        )}
                      </span>
                    </span>
                    <span className="tabular-nums text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {poids(f.octets)}
                    </span>
                    {REGARDABLE.includes(f.type) ? (
                      <button
                        type="button"
                        onClick={() => setRegarde(f)}
                        title="Regarder avant de décider"
                        className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    ) : (
                      <a
                        href={lienApercuFichier(f.chemin)}
                        target="_blank"
                        rel="noopener"
                        title="Télécharger pour l'ouvrir"
                        className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => setADetruire(f)}
                      title="Supprimer définitivement"
                      className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}

      {(data?.groupes?.length ?? 0) === 0 && (
        <div className="card text-center py-10">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Aucun fichier déposé pour l'instant.
          </p>
        </div>
      )}

      {/* Regarder avant de trancher : supprimer sans avoir vu revient à
          supprimer au hasard. */}
      {regarde && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setRegarde(null)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{regarde.nom}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {poids(regarde.octets)} ·{' '}
                  {regarde.orphelin ? 'plus référencé nulle part' : `utilisé comme ${regarde.utilisePar}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setADetruire(regarde);
                  setRegarde(null);
                }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium"
              >
                <Trash2 className="w-4 h-4" /> Supprimer
              </button>
              <button type="button" onClick={() => setRegarde(null)} className="btn-outline text-sm py-1.5">
                Fermer
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-950 grid place-items-center p-4">
              {regarde.type === 'pdf' ? (
                <iframe
                  src={lienApercuFichier(regarde.chemin)}
                  title={regarde.nom}
                  className="w-full h-[70vh] bg-white rounded"
                />
              ) : (
                <img
                  src={lienApercuFichier(regarde.chemin)}
                  alt=""
                  className="max-w-full max-h-[70vh] object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* La prévention. Elle ne se contente pas de dire « êtes-vous sûr » :
          elle dit ce qui part, ce que cela rapporte, et ce qu'on casse. */}
      {aDetruire && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-lg w-full p-5 space-y-4">
            <p className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
              Supprimer définitivement cette pièce ?
            </p>

            <div className="text-sm space-y-1 bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3">
              <p className="font-medium text-gray-900 dark:text-gray-100 break-all">{aDetruire.nom}</p>
              <p className="text-gray-600 dark:text-gray-400">
                {poids(aDetruire.octets)} récupérés · déposé le{' '}
                {jourCourt.format(new Date(aDetruire.modifie))}
              </p>
            </div>

            <p className="text-sm text-red-800 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-3 py-2">
              <strong>Récupérer de la place, oui — mais la suppression est irréversible.</strong>{' '}
              Le fichier ne part pas dans une corbeille : il est effacé du disque. Aucune
              sauvegarde ne le rendra, celles-ci ne couvrant que la base de données.
            </p>

            {!aDetruire.orphelin && (
              <p className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2">
                <strong>Cette pièce est utilisée</strong> comme {aDetruire.utilisePar}. La fiche qui
                la référence existera toujours, mais ne trouvera plus rien à afficher.
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => detruire.mutate(aDetruire.chemin)}
                disabled={detruire.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50"
              >
                {detruire.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Détruire définitivement
              </button>
              <button type="button" onClick={() => setADetruire(null)} className="btn-outline text-sm">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
